import React from "react";
import { Box, IconButton, Tooltip, Zoom, Collapse } from "@mui/material";
import {
  WhatsApp,
  Phone,
  KeyboardArrowUp,
  Message,
  Close,
} from "@mui/icons-material";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useCompany } from "../../hooks/useCompany";

const StickyContainer = styled(Box)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1000;
`;

const FloatingButton = styled(IconButton)<{
  $bgColor: string;
  href?: string;
  target?: string;
  rel?: string;
  component?: React.ElementType;
}>`
  width: 56px;
  height: 56px;
  background: ${(props) => props.$bgColor} !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  border: none !important;

  &:hover {
    background: ${(props) => props.$bgColor} !important;
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.2);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    }
  }

  animation: pulse 2s infinite;
`;

const ScrollTopButton = styled(IconButton)`
  width: 48px;
  height: 48px;
  background: rgba(26, 26, 26, 0.9) !important;
  color: white !important;
  transition: all 0.3s ease;
  border: none !important;

  &:hover {
    background: #1a1a1a !important;
    transform: translateY(-4px);
  }
`;

const StickyContact: React.FC = () => {
  const { t } = useTranslation();
  const company = useCompany();
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <StickyContainer>
      <Zoom in={showScrollTop}>
        <ScrollTopButton onClick={scrollToTop}>
          <KeyboardArrowUp />
        </ScrollTopButton>
      </Zoom>

      <Collapse in={isExpanded} timeout={300}>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: "12px", mb: 1 }}
        >
          <Tooltip title={t("stickyContact.whatsapp")} placement="left" arrow>
            <FloatingButton
              $bgColor="#25D366"
              href={company?.social?.whatsapp || "https://wa.me/905528378377"}
              target="_blank"
              component="a"
            >
              <WhatsApp sx={{ fontSize: 28 }} />
            </FloatingButton>
          </Tooltip>

          <Tooltip title={t("stickyContact.call")} placement="left" arrow>
            <FloatingButton
              $bgColor="#d32f2f"
              href={`tel:${(company?.phone || "+905528378377").replace(
                /\s+/g,
                ""
              )}`}
              component="a"
            >
              <Phone sx={{ fontSize: 28 }} />
            </FloatingButton>
          </Tooltip>
        </Box>
      </Collapse>

      <Tooltip
        title={
          isExpanded
            ? t("stickyContact.toggleClose")
            : t("stickyContact.toggleOpen")
        }
        placement="left"
        arrow
      >
        <FloatingButton
          $bgColor="#1976d2"
          onClick={toggleExpanded}
          sx={{
            transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
          }}
        >
          {isExpanded ? (
            <Close sx={{ fontSize: 28 }} />
          ) : (
            <Message sx={{ fontSize: 28 }} />
          )}
        </FloatingButton>
      </Tooltip>
    </StickyContainer>
  );
};

export default StickyContact;
